import { Component } from "../../../../commons/types/component";
import { ForkSuspensionTravel } from "../../../../commons/types/fork-suspension";
import cn from "../../libs/class-name";
import usePrevious from "../../libs/hooks/use-previous";

type State = "enabled" | "disabled";
type SuspensionLength = {
  left: string;
  right: string;
};

interface Props extends Component {
  travel: ForkSuspensionTravel;
}

const ForkSuspension = ({ travel = ForkSuspensionTravel.None, classNames = [] }: Props) => {
  // Track previous travel value for animation bounce
  const previousTravel = usePrevious(travel);

  // Calculate bounceIn based on current and previous travel values
  // On first render, previousTravel is undefined (acts like "init")
  const bounceIn: boolean =
    (travel !== "none" && (previousTravel === "none" || previousTravel === undefined)) || travel === previousTravel;

  const stabilization: State = travel === "l" ? "enabled" : "disabled";
  const suspension: State = travel !== "none" ? "enabled" : "disabled";

  const calculateSuspensionLengths = (travel: ForkSuspensionTravel): SuspensionLength => {
    const suspensionLengths: Record<ForkSuspensionTravel, SuspensionLength> = {
      none: { left: "", right: "" },
      s: {
        left: "M11.244,0L11.244,34.38L0,47.307L0,0L11.244,0Z",
        right: "M50.6,0L50.6,33.594L61.844,47.307L61.844,0L50.6,0Z"
      },
      m: {
        left: "M11.244,-25.119L11.244,34.38L0,47.307L0,-25.119L11.244,-25.119Z",
        right: "M50.6,-25.119L50.6,33.594L61.844,47.307L61.844,-25.119L50.6,-25.119Z"
      },
      l: {
        left: "M11.244,-54.658L11.244,34.38L0,47.307L0,-54.658L11.244,-54.658Z",
        right: "M50.6,-54.658L50.6,33.594L61.844,47.307L61.844,-54.658L50.6,-54.658Z"
      }
    };

    return suspensionLengths[travel];
  };

  return (
    <div
      className={cn(
        "ForkSuspension",
        [
          {
            [`travel-${travel}`]: travel,
            [`init-suspension-${travel}`]: bounceIn ? travel : false,
            [`stabilization-${stabilization}`]: stabilization,
            [`suspension-${suspension}`]: suspension
          }
        ],
        classNames
      )}
    >
      <svg
        width="254px"
        height="312px"
        viewBox="0 0 1059 1300"
        version="1.1"
        xmlSpace="preserve"
        className="ForkSuspension__with-suspension"
      >
        <g transform="matrix(4.16667,0,0,4.16667,0,0)">
          <g id="ForkSuspension-divider" transform="matrix(1,0,0,1,-201.919,26.933)">
            <g transform="matrix(0.933197,0,0,1,236.747,112.545)">
              <path
                id="ForkSuspension-divider-end"
                d="M0.562,0.563L197.34,0.563"
                className="ForkSuspension__divider ForkSuspension__divider-end"
              />
            </g>
            <g transform="matrix(0.933197,0,0,1,236.747,112.545)">
              <path
                id="ForkSuspension-divider-start"
                d="M0.562,47.87L197.34,47.87"
                className="ForkSuspension__divider ForkSuspension__divider-start"
              />
            </g>
            <g transform="matrix(0.933197,0,0,1,236.747,40.5344)">
              <path
                id="ForkSuspension-divider-medium"
                d="M0.562,47.87L197.34,47.87"
                className="ForkSuspension--hidden"
              />
            </g>
            <g transform="matrix(0.933197,0,0,1,236.747,11.0263)">
              <path
                id="ForkSuspension-divider-large"
                d="M0.562,47.87L197.34,47.87"
                className="ForkSuspension--hidden"
              />
            </g>
          </g>
          <g transform="matrix(1,0,0,1,-201.919,26.933)">
            <g transform="matrix(1,0,0,1,230.698,57.3536)">
              <rect
                id="ForkSuspension-axis"
                x="84.681"
                y="189.943"
                width="28.111"
                height="7.884"
                className="ForkSuspension__axis"
              />
            </g>
            <g transform="matrix(1,0,0,1,230.698,57.3536)">
              <rect
                id="ForkSuspension-right-nut"
                x="126.285"
                y="189.943"
                width="4.498"
                height="7.884"
                className="ForkSuspension__nut"
              />
            </g>
            <g transform="matrix(1,0,0,1,230.698,57.3536)">
              <rect
                id="ForkSuspension-left-nut"
                x="66.69"
                y="189.943"
                width="4.498"
                height="7.884"
                className="ForkSuspension__nut"
              />
            </g>
          </g>
          <g transform="matrix(1,0,0,1,28.7793,84.2866)">
            <path
              id="ForkSuspension-fork"
              d="M106.633,84.065L130.216,111.661L130.216,120.17L128.797,120.17L128.797,123.619L127.736,126.961L127.736,188.159L124.706,188.159L124.706,199.116L114.541,199.116L114.541,188.159L112.64,188.159L112.64,123.619L111.946,120.17L109.889,120.17L109.889,98.645L106.633,92.745L100.976,89.762L96.383,89.762L90.639,92.745L87.332,98.645L87.332,120.17L85.243,120.17L84.539,123.619L84.539,188.159L82.608,188.159L82.608,199.116L72.286,199.116L72.286,188.159L69.208,188.159L69.208,126.961L68.132,123.619L68.132,120.17L66.69,120.17L66.69,111.661L90.639,84.065L106.633,84.065Z"
              className="ForkSuspension__fork"
            />
          </g>
          <g id="ForkSuspension-suspension" transform="matrix(1,0,0,1,-201.919,26.933)">
            <g transform="matrix(1,0,0,1,298.165,114.99)">
              <path
                id="ForkSuspension-right-suspension"
                d={calculateSuspensionLengths(travel).right}
                className="ForkSuspension__suspension"
              />
            </g>
            <g transform="matrix(1,0,0,1,298.165,114.99)">
              <path
                id="ForkSuspension-left-suspension"
                d={calculateSuspensionLengths(travel).left}
                className="ForkSuspension__suspension"
              />
            </g>
          </g>
          <g transform="matrix(1,0,0,1,28.7793,84.2866)">
            <path
              id="ForkSuspension-forkhead"
              d="M89.63,36.759L106.321,36.759L118.447,44.478L130.245,44.478L130.245,56.11L116.793,56.11L116.793,53.542L106.321,46.434L89.63,46.434L79.852,53.542L79.852,56.11L66.128,56.11L66.128,44.478L77.802,44.478L89.63,36.759Z"
              className="ForkSuspension__forkhead"
            />
          </g>
          <g transform="matrix(1,0,0,1,34.3903,27.7984)">
            <path
              id="ForkSuspension-stabilizor"
              d="M84.049,93.247L100.749,93.247L112.881,100.981L124.684,100.981L124.684,112.634L111.225,112.634L111.225,110.061L100.749,102.94L84.049,102.94L74.267,110.061L74.267,112.634L60.535,112.634L60.535,100.981L72.216,100.981L84.049,93.247Z"
              className="ForkSuspension__stabilizor"
            />
          </g>
          <g transform="matrix(1,0,0,1,28.7793,84.2866)">
            <path
              id="ForkSuspension-shaft"
              d="M92.085,-0L104.576,-0L104.576,17.181L106.647,23.105L106.647,32.995L89.956,32.995L89.956,23.105L92.085,17.181L92.085,-0Z"
              className="ForkSuspension__shaft"
            />
          </g>
        </g>
      </svg>

      <svg width="254px" height="312px" viewBox="0 0 254 312" className="ForkSuspension__without-suspension">
        <g
          id="Illustrations-&amp;-Graphics-&amp;-Sketch-Icons"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <g className="ForkSuspension__fork-without-suspension">
            <path
              d="M134.992704,131.991704 L134.992704,136.628351 C138.26757,137.229204 140.47081,137.76507 141.602422,138.23595 C144.630482,139.49597 146.509591,140.902585 147.485732,141.506868 C149.770728,142.921401 152.639162,145.869358 155.691625,151.144833 C157.463994,154.207962 159.417712,158.239683 160.714522,162.716976 C162.011332,167.194268 161.700715,169.973177 162.339882,176.11682 C162.885112,181.357539 163.163762,190.763583 163.175832,204.334951 L163.175743,206.302054 C163.157986,224.926141 162.66631,250.95525 161.700715,284.389381 L150.636022,284.389381 C150.033398,261.372803 149.602532,244.281393 149.343421,233.115152 C148.989022,217.84249 149.084634,202.836881 148.482241,188.146304 C148.405713,186.280008 148.321115,184.095471 148.227165,181.896326 L148.189083,181.016608 C147.996148,176.621322 147.765119,172.329536 147.485732,170.570295 C147.058425,167.104997 146.724415,165.658304 146.203027,162.716976 C145.684589,159.792286 144.70033,158.10916 144.384694,157.636051 C143.01481,155.582723 142.086918,154.617837 141.116752,153.945499 C140.061906,153.214476 139.047839,152.296275 137.179828,151.766951 C134.354353,150.646042 131.358722,150.049949 128.192935,149.978673 L127.562046,149.999925 C124.628486,150.131761 121.843151,150.720769 119.206041,151.766951 C117.338031,152.296275 116.323963,153.214476 115.269118,153.945499 C114.298952,154.617837 113.37106,155.582723 112.001175,157.636051 C111.685539,158.10916 110.701281,159.792286 110.182842,162.716976 C109.661454,165.658304 109.327444,167.104997 108.900137,170.570295 C108.602124,172.446818 108.359132,177.204815 108.158705,181.896326 L108.121621,182.774485 C108.042339,184.672357 107.969953,186.528847 107.903629,188.146304 C107.301235,202.836881 107.396847,217.84249 107.042448,233.115152 C106.783338,244.281393 106.352471,261.372803 105.749848,284.389381 L94.6851545,284.389381 C92.9608777,224.685577 92.747822,188.594723 94.0459875,176.11682 C94.6851545,169.973177 94.374538,167.194268 95.6713476,162.716976 C96.9681571,158.239683 98.9218759,154.207962 100.694245,151.144833 C103.746708,145.869358 106.615141,142.921401 108.900137,141.506868 C109.876279,140.902585 111.755388,139.49597 114.783448,138.23595 C115.91506,137.76507 118.118299,137.229204 121.393165,136.628351 L121.393165,131.991704 L134.992704,131.991704 Z M148.837132,274.229535 L149.07007,282.131637 L107.3158,282.131637 L107.573182,274.229535 L148.837132,274.229535 Z M167.690217,274.229535 L167.690217,282.131637 L162.920661,282.131637 L163.176242,274.229535 L167.690217,274.229535 Z M93.2096273,274.229535 L93.4597956,282.131637 L88.6956522,282.131637 L88.6956522,274.229535 L93.2096273,274.229535 Z M121.421972,67.6460177 L134.963898,67.6460177 L134.963898,128.605088 L121.421972,128.605088 L121.421972,67.6460177 Z"
              id="filter-without-suspension"
            ></path>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default ForkSuspension;
